import React, { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface ResponsiveImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src: string;
  alt: string;
  fallbackSrc?: string;
  priority?: boolean;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
}

export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  src,
  alt,
  fallbackSrc,
  priority = false,
  className,
  onLoad,
  onError,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [retryCount, setRetryCount] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Generate responsive image sources
  const getResponsiveSrc = (baseSrc: string) => {
    // If the image already has mobile suffix, return as-is
    if (baseSrc.includes("_mobile.webp")) {
      return baseSrc;
    }

    // If the image already has PC suffix, convert to mobile
    if (baseSrc.includes("_pc.webp")) {
      return baseSrc.replace("_pc.webp", "_mobile.webp");
    }

    // Extract the base path and extension
    const lastDotIndex = baseSrc.lastIndexOf(".");
    if (lastDotIndex === -1) return baseSrc;

    const basePath = baseSrc.substring(0, lastDotIndex);
    const extension = baseSrc.substring(lastDotIndex);

    // Return the base path with mobile suffix (default)
    return `${basePath}_mobile${extension}`;
  };

  const getPCSrc = (baseSrc: string) => {
    // If the image already has PC suffix, return as-is
    if (baseSrc.includes("_pc.webp")) {
      return baseSrc;
    }

    // If the image already has mobile suffix, convert to PC
    if (baseSrc.includes("_mobile.webp")) {
      return baseSrc.replace("_mobile.webp", "_pc.webp");
    }

    // Extract the base path and extension
    const lastDotIndex = baseSrc.lastIndexOf(".");
    if (lastDotIndex === -1) return baseSrc;

    const basePath = baseSrc.substring(0, lastDotIndex);
    const extension = baseSrc.substring(lastDotIndex);

    // Return the base path with PC suffix
    return `${basePath}_pc${extension}`;
  };

  // Detect screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    // Check initial screen size
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener("resize", checkScreenSize);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) {
      setIsInView(true);
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.disconnect();
        }
      },
      {
        rootMargin: "50px", // Start loading 50px before the image comes into view
        threshold: 0.1,
      },
    );

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    // Try to retry loading the image once
    if (retryCount < 1 && imgRef.current) {
      setRetryCount((prev) => prev + 1);
      // Force reload by adding a timestamp parameter
      const separator = src.includes("?") ? "&" : "?";
      const retrySrc = `${src}${separator}t=${Date.now()}`;

      // Create a new image to test the retry
      const retryImg = new Image();
      retryImg.onload = () => {
        // If retry succeeds, update the src
        if (imgRef.current) {
          imgRef.current.src = retrySrc;
        }
      };
      retryImg.onerror = () => {
        // If retry fails, show error state
        setHasError(true);
        onError?.();
      };
      retryImg.src = retrySrc;
    } else {
      setHasError(true);
      onError?.();
    }
  };

  const finalSrc =
    hasError && fallbackSrc ? fallbackSrc : getResponsiveSrc(src);
  const pcSrc = getPCSrc(src);

  // Choose the appropriate image source based on screen size
  const currentSrc = isDesktop ? pcSrc : finalSrc;

  // Debug logging (remove in production)
  if (process.env.NODE_ENV === "development" && src.includes("family-suite")) {
    console.log("ResponsiveImage Debug:", {
      originalSrc: src,
      mobileSrc: finalSrc,
      pcSrc: pcSrc,
      isDesktop: isDesktop,
      currentSrc: currentSrc,
    });
  }

  return (
    <div
      className={cn("relative overflow-hidden", className)}
      data-oid="vulqumw"
    >
      {/* Loading placeholder */}
      {!isLoaded && !hasError && (
        <div
          className="absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center"
          data-oid=":p9xpb1"
        >
          <div
            className="w-8 h-8 border-2 border-gray-300 border-t-alpine-gold rounded-full animate-spin"
            data-oid="65e3f2g"
          />
        </div>
      )}

      {/* Image element with responsive sources */}
      {isInView && (
        <img
          ref={imgRef}
          key={`${currentSrc}-${isDesktop}`}
          src={currentSrc}
          alt={alt}
          className={cn(
            "w-full h-full object-cover transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0",
          )}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          {...props}
          data-oid="q1fdpn6"
        />
      )}

      {/* Error state */}
      {hasError && (
        <div
          className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"
          data-oid="dai7m::"
        >
          <div className="text-center text-gray-500" data-oid="hvccra3">
            <div
              className="w-16 h-16 mx-auto mb-3 bg-gray-300 rounded-lg flex items-center justify-center"
              data-oid="x0x0co3"
            >
              <svg
                className="w-8 h-8 opacity-60"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                data-oid="xmj-:.q"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  data-oid="0od.mg1"
                />
              </svg>
            </div>
            <p className="text-sm font-medium" data-oid=".y:wl84">
              Image unavailable
            </p>
            <button
              onClick={() => {
                setHasError(false);
                setRetryCount(0);
                setIsLoaded(false);
              }}
              className="mt-2 px-3 py-1 bg-alpine-gold text-white text-xs rounded-md hover:bg-alpine-gold/90 transition-colors"
              data-oid="ov5_cf8"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponsiveImage;
